// src/components/NotificationsDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bell, CheckCircle, X, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';

const NotificationsDropdown = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser, csrfToken } = useAuth();
  const dropdownRef = useRef(null);
  
  // Cargar notificaciones
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
      setError('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };
  
  // Marcar como leída
  const markAsRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}`, { _csrf: csrfToken });
      await fetchNotifications();
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };
  
  // Cargar notificaciones al montar el componente
  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
      // Recargar notificaciones cada minuto
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);
  
  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        className="text-gray-400 p-1 relative"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
            {unreadCount}
          </span>
        )}
      </button>
      
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-gray-800 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="border-b border-gray-700 p-3 flex items-center justify-between">
            <h3 className="font-semibold text-white">Notificaciones</h3>
            <button 
              className="text-gray-400 hover:text-white"
              onClick={() => setShowDropdown(false)}
            >
              <X size={16} />
            </button>
          </div>
          
          {loading && (
            <div className="flex justify-center items-center p-4">
              <Loader size={20} className="animate-spin text-blue-500" />
            </div>
          )}
          
          {error && (
            <div className="p-4 text-center bg-red-900 bg-opacity-25">
              <div className="flex items-center justify-center">
                <AlertCircle size={16} className="text-red-500 mr-2" />
                <p className="text-sm text-red-500">{error}</p>
              </div>
              <button 
                className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded"
                onClick={fetchNotifications}
              >
                Reintentar
              </button>
            </div>
          )}
          
          {!loading && !error && notifications.length === 0 && (
            <div className="p-6 text-center text-gray-400">
              No tienes notificaciones
            </div>
          )}
          
          {!loading && !error && notifications.map(notification => (
            <div 
              key={notification.id} 
              className={`px-4 py-3 border-b border-gray-700 hover:bg-gray-700 ${!notification.read ? 'bg-gray-700 bg-opacity-50' : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-sm text-white">{notification.message}</p>
                  <div className="flex items-center mt-1">
                    <p className="text-xs text-gray-400">
                      {notification.sender_name || notification.sender_username || 'Sistema'}
                    </p>
                    <span className="mx-1 text-gray-500">•</span>
                    <p className="text-xs text-gray-400">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                {!notification.read && (
                  <button 
                    className="text-blue-400 hover:text-blue-300 ml-2"
                    onClick={() => markAsRead(notification.id)}
                    title="Marcar como leída"
                  >
                    <CheckCircle size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
